import AlreadySignedUpDialog from "@components/AlreadySignedUpDialog"
import Button from "@components/Button"
import Input from "@components/Input"
import PasswordInput from "@components/PasswordInput"
import FunnelHeader from "@components/quiz/FunnelHeader"
import FunnelLayout from "@components/quiz/FunnelLayout"
import StickyFooter from "@components/quiz/StickyFooter"
import createAccount from "@helpers/createAccount"
import { validateEmail, validatePassword } from "@helpers/validations"
import { IsUserSignedInLoaderData } from "@loaders/isUserSignedInLoader"
import { useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { Link, Navigate, useLoaderData, useNavigate } from "react-router-dom"

const PREPARING_PATH = "/sign-up/preparing"

/**
 * Sign-up for visitors who reach the paywall without an account (e.g. a link
 * straight to the plans). Asks for email and password together and creates
 * the account on submit: nothing is kept between steps, so it works when the
 * browser blocks storage too.
 */
export default function SignUpPage() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [emailValid, setEmailValid] = useState(true)
  const [passwordValid, setPasswordValid] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasFailed, setHasFailed] = useState(false)
  const [openAlreadySignedUpDialog, setOpenAlreadySignedUpDialog] =
    useState(false)

  const { isUserSignedIn } = useLoaderData() as IsUserSignedInLoaderData
  const navigate = useNavigate()
  const { t } = useTranslation()

  if (isUserSignedIn) return <Navigate replace to={PREPARING_PATH} />

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const isEmailValid = validateEmail(email)
    const isPasswordValid = validatePassword(password)

    setEmailValid(isEmailValid)
    setPasswordValid(isPasswordValid)
    setHasFailed(false)

    if (!isEmailValid || !isPasswordValid) return

    setIsSubmitting(true)

    const result = await createAccount({ email, password })

    if (result === "created") {
      navigate(PREPARING_PATH)
      return
    }

    if (result === "alreadyRegistered") {
      setOpenAlreadySignedUpDialog(true)
    } else {
      setHasFailed(true)
    }

    setIsSubmitting(false)
  }

  return (
    <>
      <FunnelLayout header={<FunnelHeader />}>
        <form
          className="flex grow animate-slide-in-right flex-col gap-6 pt-4"
          noValidate
          onSubmit={(event) => void handleSubmit(event)}
        >
          <div className="animate-fade-up space-y-3">
            <h1 className="vm-heading">{t("pages.signUp.email.heading")}</h1>

            <p className="vm-subheading">
              {t("pages.signUp.email.subheading")}
            </p>
          </div>

          <div className="animate-fade-up space-y-3 [animation-delay:120ms]">
            <Input
              autoComplete="email"
              autoFocus
              inputMode="email"
              invalid={!emailValid}
              name="email"
              onChange={(event) => {
                setEmail(event.target.value)
                setEmailValid(true)
              }}
              placeholder={t("shared.email")}
              type="email"
              value={email}
            />

            {!emailValid && (
              <p
                className="animate-fade-in text-sm font-medium text-vm-red"
                role="alert"
              >
                {t("pages.signUp.email.emailNotValid")}
              </p>
            )}

            <PasswordInput
              autoComplete="new-password"
              invalid={!passwordValid}
              name="password"
              onChange={(event) => {
                setPassword(event.target.value)
                setPasswordValid(true)
              }}
              value={password}
            />

            <p
              className={`text-sm ${passwordValid ? "text-vm-muted" : "font-medium text-vm-red"}`}
              role={passwordValid ? undefined : "alert"}
            >
              {t("pages.signUp.password.subheading")}
            </p>

            {hasFailed && (
              <p
                className="animate-fade-in text-sm font-medium text-vm-red"
                role="alert"
              >
                {t("pages.onboarding.password.errors.unknown")}
              </p>
            )}
          </div>

          <StickyFooter>
            <div className="space-y-4">
              <Button
                disabled={email.length < 1 || password.length < 1}
                submitting={isSubmitting}
                title={t("shared.continue")}
                type="submit"
              />

              <p className="text-center text-xs leading-relaxed text-vm-muted">
                <Trans
                  components={{
                    privacyPolicyLink: (
                      <Link
                        className="underline"
                        target="_blank"
                        to={"https://www.visualmind.app/privacy.html"}
                      />
                    ),
                    termsAndConditionsLink: (
                      <Link
                        className="underline"
                        target="_blank"
                        to={"https://www.visualmind.app/terms.html"}
                      />
                    ),
                  }}
                  i18nKey="pages.signUp.email.text"
                />
              </p>
            </div>
          </StickyFooter>
        </form>
      </FunnelLayout>

      <AlreadySignedUpDialog
        open={openAlreadySignedUpDialog}
        onClose={() => setOpenAlreadySignedUpDialog(false)}
      />
    </>
  )
}
